/**
 * Shared helpers for the theme's SVG drawings (doors, cabinets, kitchen
 * scenes, drilling views).
 *
 * <p>The drawings are built as strings by pure functions, so the same code runs
 * on the server, in the browser and in tests, and a builder can redraw on every
 * keystroke without React reconciling hundreds of nodes. They are inserted with
 * {@link SvgArt}.
 *
 * <p>Two rules keep that safe and hydration-stable:
 * <ol>
 *   <li>Every piece of text that reaches the markup goes through {@link esc}.
 *       Numbers are formatted with {@link n}, which refuses NaN and Infinity.</li>
 *   <li>Gradient and pattern ids come from an {@link IdSource} seeded by the
 *       caller (normally React's `useId`), never from a module counter, so the
 *       server and the browser produce the same markup.</li>
 * </ol>
 */

/** Escapes text for use inside SVG/XML content or a double-quoted attribute. */
export function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** A number for an SVG attribute: rounded to 2 dp, and 0 rather than NaN or Infinity. */
export function n(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

/** Hands out ids unique within one drawing, all under one caller-given prefix. */
export interface IdSource {
  next(): string;
}

export function idSource(prefix: string): IdSource {
  // useId() returns ids like ":r1:" which are not valid in url(#...) references.
  const safe = prefix.replace(/[^A-Za-z0-9_-]/g, '') || 'sk';
  let count = 0;
  return { next: () => `${safe}-${++count}` };
}

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** A colour safe to put in a fill: a `#rgb`/`#rrggbb` hex, else the fallback. */
export function safeHex(value: unknown, fallback = '#8a9a82'): string {
  return typeof value === 'string' && HEX.test(value) ? value : fallback;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = safeHex(hex).slice(1);
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const value = parseInt(full, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** Darkens (negative `amount`, down to -1) or lightens (positive, up to 1) a hex colour. */
export function shade(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = (c: number) =>
    Math.max(0, Math.min(255, Math.round(amount < 0 ? c * (1 + amount) : c + (255 - c) * amount)));
  return `#${[f(r), f(g), f(b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}
